import { FC, UIEvent } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import {
  FeaturedCard,
  GameLibraryItem,
  getGameLibraryGQL,
} from "@onmo/graphql/graphql";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useStyles } from "../GameCategory/GameCategory.style";
import ArrowWrapper from "../ArrowWrapper/ArrowWrapper";
import { FeaturedCardItem } from "../GameCategory/FeaturedCardItem";
import { isTV } from "../../../utils/platform";
import { LIBRARY_GAME_SIZE } from "../../../constants/gamePack";

interface IMyLibraryCategoryProps {
  gameCategory: "BYOG" | "PID";
  group: string;
  defaultCardWidth?: number;
}

type GameLibraryQueryData = {
  me?: {
    gameLibrary?: {
      items: GameLibraryItem[];
      total: number;
    };
  };
};

export const MyLibraryCategory: FC<IMyLibraryCategoryProps> = ({
  gameCategory,
  group,
  defaultCardWidth,
}) => {
  const classes = useStyles();
  const { data, fetchMore, loading, networkStatus } =
    useQuery<GameLibraryQueryData>(getGameLibraryGQL, {
      variables: {
        input: {
          filter: { gameCategory },
          page: { limit: LIBRARY_GAME_SIZE, offset: 0 },
        },
      },
      notifyOnNetworkStatusChange: true,
    });
  const library = data?.me?.gameLibrary;
  const items = library?.items ?? [];
  const isLoading = loading || networkStatus === NetworkStatus.fetchMore;
  const hasMore = items.length < (library?.total ?? 0);

  const onLoadMore = async () => {
    if (isLoading || !hasMore) return;

    await fetchMore({
      variables: {
        input: {
          filter: { gameCategory },
          page: { limit: LIBRARY_GAME_SIZE, offset: items.length },
        },
      },
    });
  };

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft, clientWidth, scrollWidth } = event.currentTarget;
    if (
      hasMore &&
      !isLoading &&
      scrollLeft + clientWidth >= scrollWidth - clientWidth / 2
    ) {
      onLoadMore();
    }
  };

  if (!items.length) return null;

  const cards = items
    .filter((item) => Boolean(item.game.id))
    .map(
      (item) =>
        ({
          id: item.id,
          group,
          data: { game: item.game },
        } as FeaturedCard),
    );

  if (!cards.length) return null;

  const cardItems = cards.map((card) => (
    <FeaturedCardItem
      key={card.id}
      featuredCard={card}
      defaultCardWidth={defaultCardWidth}
      classes={classes}
      sessionId={group}
    />
  ));

  return (
    <section>
      <Typography
        variant={TypographyVariant.BodyB1Medium}
        className={classes.title}
      >
        My Library
      </Typography>
      {isTV ? (
        <div className={classes.content} onScroll={onScroll}>
          {cardItems}
        </div>
      ) : (
        <ArrowWrapper
          className={classes.content}
          id={`content-games-${group}`}
          onScroll={onScroll}
        >
          {cardItems}
        </ArrowWrapper>
      )}
    </section>
  );
};
